import {useState} from "react";
import {
    RoomTypeInput,
    RoomTypesFromDataQuery,
    SaveRoomTypeMutation,
    SaveRoomTypeMutationVariables
} from 'types/graphql';
import Input from "components/common/form/inputs/Input";
import {gql} from "@apollo/client";
import PageForm, {PageFormProps} from "components/common/page/PageForm";
import {uuid} from "../../../config/app-constants";
import BookingChannelDropdownInput
    , { BookingChannelDropdownDataItemType }    from "../../../components/entities/accommodation/channel/BookingChannelDropdownInput";
import useAppContext from "utilities/hooks/useAppContext";
import ColorInput from "@/components/common/form/inputs/ColorInput";

type ChannelDefaultPrice = {
    id: number | string,
    chnnelId: number,
    name: string,
    defaultPrice: number | string
}

type RoomTypeForm = Omit<RoomTypeInput, 'defaultPrice'> & {
    defaultPrice: number | string;
    channelDefaultPrices: ChannelDefaultPrice[]
}

const query = gql`
query RoomTypesFromData($id: Int) {
  roomType(id: $id) {
    id
    name
    defaultPrice
    color
    channelDefultPrices {
      items {
        channel {
          id
          name
        }
        defaultPrice
        id
      }
    }
  }
}
`;

const mutation = gql`
mutation SaveRoomType($model: RoomTypeInput!)
{
  roomType(model: $model) {
    id
  }
}
`;

const RoomTypeFormPage = () => {
    const context = useAppContext();
    const [model, setModel] = useState<RoomTypeForm>({
        color: '',
        defaultPrice: '',
        id: 0,
        name: '',
        channelDefaultPrices: []
    });

    const formPageConfig: PageFormProps<RoomTypesFromDataQuery, SaveRoomTypeMutation, SaveRoomTypeMutationVariables> = {
        name: 'pages-accommodation-roomType-form',
        title: 'pages.accommodation.roomType.form',
        load: {
            resolveQueyData(data) {
                if (data?.roomType) {
                    setModel({
                        color: data.roomType.color,
                        defaultPrice: data.roomType.defaultPrice || '',
                        id: data.roomType.id,
                        name: data.roomType.name,
                        channelDefaultPrices: data.roomType.channelDefultPrices.items.map(x => ({
                            id: x.id,
                            chnnelId: x.channel.id,
                            name: x.channel.name,
                            defaultPrice: x.defaultPrice
                        }))
                    });
                }
            },
            query,
        },
        onSubmit: {
            mutation,
            resolveMutationData() {
                return {
                    model: {
                        id: model.id,
                        name: model.name,
                        defaultPrice: Number(model.defaultPrice),
                        color: model.color,
                        channelDefultPrices: model.channelDefaultPrices.map(i => ({
                            channelId: i.chnnelId,
                            defaultPrice: Number(i.defaultPrice),
                            id: Number(i.id) || 0,
                            roomTypeId: Number(model.id) || 0
                        }))
                    }
                }
            },
        }
    }

    const addChannelDefaultPrice = (channel?: BookingChannelDropdownDataItemType) => {
        if(!channel) return;
        if(model.channelDefaultPrices.some(x => x.chnnelId === channel.id)) {
            context.notify('error', 'form.errors.unique');
            return;
        }

        const cdp = model.channelDefaultPrices || [];
        cdp.push({
            chnnelId: channel.id,
            defaultPrice: '',
            id: uuid(),
            name: channel.name
        })
        setModel({
            ...model,
            channelDefaultPrices: cdp
        });
    }

    const onDefaultPriceChange = (index: number, defaultPrice?: string | number) => {
        const m = {...model}
        m.channelDefaultPrices[index].defaultPrice = defaultPrice || '';
        setModel(m);
    }

    return (
        <>
            <PageForm {...formPageConfig}>
                <fieldset>
                    <Input
                        label="fields.name.name"
                        maxLength={128}
                        required
                        value={model.name}
                        onChange={v => setModel({
                            ...model,
                            name: v || ''
                        })}
                    />
                    <ColorInput
                        required
                        label="fields.color.name"
                        value={model.color}
                        onChange={v => setModel({
                            ...model,
                            color: v || ''
                        })}
                    />
                    <Input
                        label="fields.defaultPrice.name"
                        type="number"
                        required
                        value={model.defaultPrice?.toString()}
                        onChange={v => setModel({
                            ...model,
                            defaultPrice: v || ''
                        })}
                    />
                </fieldset>
                <fieldset>
                    <hr/>
                    {model.channelDefaultPrices?.map((item, index) => {
                        return (
                            <Input
                                key={item.id}
                                type="number"
                                required
                                label={`${item.name} ${context.t('fields.defaultPrice.name')}`}
                                value={item.defaultPrice?.toString() || ''}
                                onChange={v => onDefaultPriceChange(index, v)}
                            />
                        )
                    })}
                    <div className="border-b border-dashed w-full theme-border"></div>
                    <BookingChannelDropdownInput
                        placeholder="Select channel to add"
                        onChange={addChannelDefaultPrice}
                    />
                </fieldset>
            </PageForm>
        </>
    );
};

export default RoomTypeFormPage;
